import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Box, Stack, Typography, TextField, Button, IconButton, FreeSoloDropdown, Grid} from 'views/components';
// Import IconButton
import {AddCircleOutline as AddCircleOutlineIcon, Delete as DeleteIcon} from 'views/icons';
import {Translate, translate, MarkdownEditor} from 'views/module';

const RelationshipEdit = (props) => {
  const [editableData, setEditableData] = React.useState({
    name: '',
    connectionType: '',
    priority: 1,
    description: {
      blob: '',
      editorType: 'MARKDOWN',
    },
    contacts: [],
  });

  const {editRelationship, relationship, onClose} = props;

  React.useEffect(() => {
    setEditableData(relationship);
  }, [relationship]);

  const handleChange = (field, value) => {
    setEditableData({
      ...editableData,
      [field]: value,
    });
  };

  const handleContactChange = (index, field, value) => {
    const updatedContacts = [...editableData.contacts];
    updatedContacts[index] = {
      ...updatedContacts[index],
      [field]: value,
    };
    setEditableData({
      ...editableData,
      contacts: updatedContacts,
    });
  };

  const handleAddContact = () => {
    setEditableData({
      ...editableData,
      contacts: [...editableData.contacts, {}], // Add an empty contact
    });
  };

  const handleRemoveContact = (index) => {
    const updatedContacts = editableData.contacts.filter((_, i) => i !== index); // Remove the contact by index
    setEditableData({
      ...editableData,
      contacts: updatedContacts,
    });
  };

  const handleSave = () => {
    editRelationship(editableData.relationshipRn, editableData);
  };


  const isFormValid = React.useCallback(() => {
    return editableData.name && editableData.connectionType && editableData.priority;
  }, [editableData]);


  return (
    <Dialog onClose={onClose} open={true}>
      <DialogTitle onClose={onClose}>
        <Translate expression='client.ClientDetail.Relationships.EditRelationship' />
      </DialogTitle>
      <DialogContent>
        <Box>
          <Stack spacing={2}>
            <Box>
              <Typography variant="h6"></Typography>
            </Box>
            <TextField
              label={<Translate expression='client.ClientDetail.Relationships.Details.Name' />}
              value={editableData.name}
              onChange={(e) => handleChange('name', e)}
              fullWidth
            />
            <TextField
              label={<Translate expression='client.ClientDetail.Relationships.Details.ConnectionType' />}
              value={editableData.connectionType}
              onChange={(e) => handleChange('connectionType', e)}
              fullWidth
            />
            <TextField
              label={<Translate expression='client.ClientDetail.Relationships.Details.Priority' />}
              value={editableData.priority}
              onChange={(e) => handleChange('priority', e)}
              fullWidth
              type="number"
            />
            <Box>
              <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                <Translate expression='client.ClientDetail.Relationships.Details.Contacts' />
              </Typography>
              <Stack spacing={1}>
                {
                  editableData.contacts.map((contact, index) => (
                    <Grid container alignItems="center" key={index}>
                      <Grid item xs={5.5}>
                        <FreeSoloDropdown
                          options={[
                            translate('client.ClientDetail.Relationships.Details.ContactTypeExample.Email'),
                            translate('client.ClientDetail.Relationships.Details.ContactTypeExample.PhoneNumber'),
                            translate('client.ClientDetail.Relationships.Details.ContactTypeExample.EmergencyContact'),
                          ]}
                          label={translate('client.ClientDetail.Relationships.Details.ContactType')}
                          onChange={(e) => handleContactChange(index, 'type', e)}
                          value={contact.type}
                        />
                      </Grid>
                      <Grid item xs={5.5}
                        sx={{
                          px: 1,
                        }}
                      >
                        <TextField
                          label={<Translate expression='client.ClientDetail.Relationships.Details.ContactDetail' />}
                          value={contact.detail || ''}
                          onChange={(e) => handleContactChange(index, 'detail', e)}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <IconButton
                          onClick={() => handleRemoveContact(index)} // Remove the contact
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))
                }
                <Button
                  startIcon={<AddCircleOutlineIcon />}
                  onClick={handleAddContact}
                >
                  <Translate expression='client.ClientDetail.Relationships.Details.AddNewContact' />
                </Button>
              </Stack>
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{fontWeight: 500}}>
                <Translate expression='generic.Description'/>
              </Typography>
              <MarkdownEditor
                value={editableData.description.blob}
                onChange={(e) => handleChange('description', {blob: e, editorType: 'MARKDOWN'})}
              />
            </Box>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={onClose}>
          <Translate expression='generic.Close' />
        </Button>
        <Button color="primary" variant="contained" onClick={() => handleSave()} disabled={!isFormValid()}>
          <Translate expression='client.ClientDetail.Relationships.EditRelationship' />
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RelationshipEdit.propTypes = {
  relationship: PropTypes.object.isRequired,
  editRelationship: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RelationshipEdit;
